import {
  Box,
  Button,
  Checkbox,
  HStack,
  SimpleGrid,
  Skeleton,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

const PRESETS = [
  { key: 'all', label: 'All' },
  { key: 'none', label: 'None' },
  { key: 'match', label: 'Match table' },
];

const FieldChecklist = ({
  fields,
  isLoading,
  selectedFields,
  onToggleField,
  onApplyPreset,
  matchTableKeys = [],
}) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor = useColorModeValue('secondaryGray.600', 'white');

  const groups = useMemo(() => {
    const byGroup = new Map();
    fields.forEach((field) => {
      if (!byGroup.has(field.group)) byGroup.set(field.group, []);
      byGroup.get(field.group).push(field);
    });
    return Array.from(byGroup.entries());
  }, [fields]);

  if (isLoading) {
    return (
      <Box>
        <Skeleton height="20px" mb={3} />
        <Skeleton height="80px" mb={2} />
        <Skeleton height="80px" />
      </Box>
    );
  }

  return (
    <Box>
      <HStack spacing={2} mb={3}>
        {PRESETS.map((preset) => (
          <Button
            key={preset.key}
            size="xs"
            bg="brand.100"
            color="brand.600"
            _hover={{ bg: 'brand.500', color: 'white' }}
            borderRadius="8px"
            onClick={() => onApplyPreset(preset.key, matchTableKeys)}
          >
            {preset.label}
          </Button>
        ))}
      </HStack>
      <Box>
        {groups.map(([groupName, groupFields]) => (
          <Box key={groupName} mb={3}>
            <Text
              fontSize="xs"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="0.04em"
              color={mutedColor}
              mb={1}
            >
              {groupName}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={1}>
              {groupFields.map((field) => (
                <Checkbox
                  key={field.key}
                  size="sm"
                  colorScheme="brand"
                  sx={{ '.chakra-checkbox__control': { borderRadius: '5px' } }}
                  isChecked={selectedFields.has(field.key)}
                  onChange={() => onToggleField(field.key)}
                >
                  <Text color={textColor} fontSize="sm">
                    {field.label}
                  </Text>
                </Checkbox>
              ))}
            </SimpleGrid>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

FieldChecklist.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      group: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  selectedFields: PropTypes.instanceOf(Set).isRequired,
  onToggleField: PropTypes.func.isRequired,
  onApplyPreset: PropTypes.func.isRequired,
  matchTableKeys: PropTypes.arrayOf(PropTypes.string),
};

export default FieldChecklist;
